/* eslint-disable no-console */
import { DialogTitle, DialogContent, DialogActions, Divider } from "@mui/material";
import { media, useWindowSize, screenSizeBreakpoints, styled } from "@iventis/styles";
import React, { FunctionComponent, Ref, useRef, useState } from "react";
import { CustomRadioSelectorRef, FormButtonsComponent, StyledCustomDialog } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { MapExportApiCreateRequest } from "@iventis/domain-model/model/mapExportApiCreateRequest";
import { MapExport as MapExportApi } from "@iventis/domain-model/model/mapExport";
import { ExportFormat } from "@iventis/domain-model/model/exportFormat";
import { PageOrientation } from "@iventis/domain-model/model/pageOrientation";
import { MapExportLegendPlacement } from "@iventis/domain-model/model/mapExportLegendPlacement";
import { PageSize } from "@iventis/domain-model/model/pageSize";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { v4 as uuid } from "uuid";
import { createMapExportNotification, useNotificationContext } from "@iventis/notifications";
import { exportMapMonitoringMetric } from "@iventis/observability-and-monitoring";
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";
import { parseEpochSecondsToFormattedDateString, parseTimeHHMMNumbertoHHMMString } from "@iventis/utilities";
import { getSitemapLevelsWithinBoundsAndLevelIndex } from "@iventis/map-engine/src/utilities/sitemap-helpers";
import bboxPolygon from "@turf/bbox-polygon";
import { MapPreview, MapPreviewProps, MapPreviewRef } from "./map-export-preview";
import { ExportMapForm } from "./export-map-form";
import {
    ALL_PAGE_SIZES,
    getAllAttributeListItemsAffectingLayerStyle,
    getExportedMapHeightAndWidth,
    getScaledBounds,
    getUniqueModels,
    parseLayerToExportMapLayers,
    parseNodesToExportMapNodes,
} from "./export-helpers";

export type MapExport = Omit<MapExportApi, "apiKey" | "export"> & { export: Pick<MapExportApi["export"], "notificationId"> };

type ExportMapDialogProps = Omit<MapPreviewProps, "exportFormValues" | "ref"> & {
    isOpen: boolean;
    onClose: () => void;
    mapLayers: MapLayer[];
    levelIndex: number;
    selectedSitemapVersions: SitemapVersion[];
    userId: string;
    projectId: string;
    dateFilter?: string;
    timeFilter?: string;
    paperSizes: undefined | PageSize[];
};

export type SelectedFormOption = {
    pageOrientation: PageOrientation;
    pageSize: PageSize;
    fileFormat: ExportFormat;
    legendPosition: MapExportLegendPlacement;
    customLogo: string;
    remarks: string;
    scale: string;
    title: string;
};

export type FormOptions = {
    title: string;
    id: string;
    type: string;
    optionValues?: { id: string; name: string; isPlaceHolder?: boolean }[];
    placeholder?: string;
    disabled?: boolean;
    error?: string;
    ref?: Ref<CustomRadioSelectorRef>;
}[];

export const ExportMapDialog: FunctionComponent<ExportMapDialogProps> = ({
    map,
    isOpen,
    onClose,
    initialState,
    mapApiFunctions,
    signatures,
    userFirstName,
    userLastName,
    mapLayers,
    layersWithTemporaryVisibilityValue,
    getMapStartingPosition,
    projectLogo,
    projectName,
    levelIndex,
    selectedSitemapVersions = [],
    userId,
    projectId,
    dateFilter,
    timeFilter,
    paperSizes,
}) => {
    const { screenWidth } = useWindowSize();

    const translate = useIventisTranslate();

    const { createNotification } = useNotificationContext();

    const pageOrientationRadioButtonRef = useRef<CustomRadioSelectorRef>();

    const [isSendingExportRequest, setIsSendingExportRequest] = useState(false);

    const dateTimeFilterValue = initialState.datesFilter.value;

    const formattedDate =
        dateTimeFilterValue.filter && `${parseEpochSecondsToFormattedDateString(dateTimeFilterValue.day)} ${parseTimeHHMMNumbertoHHMMString(dateTimeFilterValue.time)}`;

    const [selectedFormOption, setSelectedFormOption] = useState<SelectedFormOption>({
        pageOrientation: PageOrientation.Landscape,
        pageSize: PageSize.A4,
        fileFormat: ExportFormat.Pdf,
        legendPosition: MapExportLegendPlacement.Right,
        customLogo: null,
        remarks: "",
        scale: "Custom",
        title: dateTimeFilterValue.filter ? `${map.name} - ${formattedDate}` : map.name,
    });

    const handleChange = (fieldChanged: { id: string; name: string }) => {
        const { id, name } = fieldChanged;
        const updateValues = { ...selectedFormOption, [id]: name };
        // Orientation has different legend position values, select a valid one
        if (id === "pageOrientation") {
            updateValues.legendPosition = name === "Landscape" ? MapExportLegendPlacement.Right : MapExportLegendPlacement.Top;
            pageOrientationRadioButtonRef.current.setValueExternally(updateValues.legendPosition);
        }
        updateMapToScale(updateValues);
        setSelectedFormOption(updateValues);
    };

    const updateMapToScale = async (formOptions: SelectedFormOption) => {
        if (formOptions.scale === "Custom") {
            return;
        }
        const position = await mapPreviewRef.current.getPosition();
        const dimensions = getExportedMapHeightAndWidth(
            formOptions.pageOrientation,
            formOptions.pageSize,
            formOptions.fileFormat === ExportFormat.Pdf && formOptions.legendPosition !== MapExportLegendPlacement.Hidden
        );
        const bounds = getScaledBounds(Number(formOptions.scale), dimensions.width, dimensions.height, { lng: position.lng, lat: position.lat }, 300);
        mapPreviewRef.current.setBounds(bounds);
        mapPreviewRef.current.setBearing(position.bearing);
    };

    const mapPreviewRef = useRef<MapPreviewRef>(undefined);

    const handleConfirm = async () => {
        setIsSendingExportRequest(true);
        exportMapMonitoringMetric(selectedFormOption.fileFormat, { resourceId: map.id });
        const { bearing, bounds, pitch } = mapPreviewRef.current.getExportPosition();
        const sitemapsLevelName = mapPreviewRef.current.getSitemapsLevelName();
        const layersToDataDrivenListItems = await getAllAttributeListItemsAffectingLayerStyle(mapLayers, mapApiFunctions.getAttributeListItems);
        const mapExportLayers = parseLayerToExportMapLayers(mapLayers, layersToDataDrivenListItems);
        const mapExportNodes = parseNodesToExportMapNodes(map.childNodes);
        const poly = bboxPolygon(bounds.flat() as [number, number, number, number]);
        const sitemapVersionLevelsWithinView = getSitemapLevelsWithinBoundsAndLevelIndex(poly.geometry.coordinates, selectedSitemapVersions, levelIndex);
        const mapExportOptions: MapExportApiCreateRequest = {
            title: selectedFormOption.title === "" ? map.name : selectedFormOption.title,
            pageSize: selectedFormOption.pageSize,
            orientation: selectedFormOption.pageOrientation,
            bounds,
            bearing,
            format: selectedFormOption.fileFormat,
            mapLayers: mapExportLayers,
            models: [],
            groups: mapExportNodes,
            legendPosition: selectedFormOption.legendPosition,
            userFirstName,
            userLastName,
            sitemapLevel: levelIndex,
            remarks: selectedFormOption.remarks,
            customLogoBase64: selectedFormOption.customLogo,
            siteMapVersionIds: sitemapVersionLevelsWithinView.map((svl) => svl.sitemapVersionId),
            notificationId: uuid(),
            dateFilter,
            timeFilter,
            sitemapsLevelName,
            scaleFactor: selectedFormOption.scale === "Custom" ? null : Number(selectedFormOption.scale),
            pitch,
            backgroundMapId: initialState.selectedMapBackgroundId,
        };

        createNotification(createMapExportNotification(userId, projectId, mapExportOptions));

        const models = await getUniqueModels(mapLayers, mapApiFunctions.multipleModelsGetter);
        mapExportOptions.models = models;

        mapApiFunctions.exportMap(mapExportOptions, map.id);

        setIsSendingExportRequest(false);
        onClose();
    };

    const isRemarksTooLong = selectedFormOption.remarks.length > 120;

    const availablePaperSizes = paperSizes == null ? ALL_PAGE_SIZES : paperSizes.map((paperSize) => ALL_PAGE_SIZES.find(({ id }) => id === paperSize));

    const formOptions = [
        {
            title: translate(Content.map5.export_map.orientation.title),
            id: "pageOrientation",
            type: "radio",
            optionValues: [
                { id: PageOrientation.Landscape, name: translate(Content.map5.export_map.orientation.landscape) },
                { id: PageOrientation.Portrait, name: translate(Content.map5.export_map.orientation.portrait) },
            ],
        },
        {
            title: translate(Content.map6.exports.legendPosition),
            id: "legendPosition",
            type: "radio",
            optionValues:
                selectedFormOption.pageOrientation === PageOrientation.Portrait
                    ? [
                          { id: MapExportLegendPlacement.Top, name: translate(Content.map6.exports.top) },
                          { id: MapExportLegendPlacement.Bottom, name: translate(Content.map6.exports.bottom) },
                          { id: MapExportLegendPlacement.Hidden, name: translate(Content.map6.exports.none) },
                      ]
                    : [
                          { id: MapExportLegendPlacement.Left, name: translate(Content.map6.exports.left) },
                          { id: MapExportLegendPlacement.Right, name: translate(Content.map6.exports.right) },
                          { id: MapExportLegendPlacement.Hidden, name: translate(Content.map6.exports.none) },
                      ],
            disabled: selectedFormOption.fileFormat === ExportFormat.Png,
            ref: pageOrientationRadioButtonRef,
        },
        {
            title: translate(Content.map5.export_map.page_size.title),
            id: "pageSize",
            type: "select",
            optionValues: availablePaperSizes,
        },
        {
            title: translate(Content.map5.export_map.file_format.title),
            id: "fileFormat",
            type: "select",
            optionValues: [
                { id: ExportFormat.Pdf, name: "PDF" },
                { id: ExportFormat.Png, name: "PNG" },
            ],
        },
        {
            title: translate(Content.map9.exports2.title),
            id: "title",
            type: "text",
            placeholder: map.name,
        },
        {
            title: translate(Content.map9.exports2.imageUpload.title),
            id: "customLogo",
            type: "file-upload",
            disabled: selectedFormOption.fileFormat === ExportFormat.Png,
        },
        {
            title: translate(Content.map5.export_map.scale),
            id: "scale",
            type: "select",
            optionValues: [
                { id: "Custom", name: "Custom" },
                { id: "200", name: "1:200" },
                { id: "500", name: "1:500" },
                { id: "1000", name: "1:1000" },
                { id: "1250", name: "1:1250" },
                { id: "2500", name: "1:2500" },
                { id: "5000", name: "1:5000" },
                { id: "10000", name: "1:10000" },
            ],
        },
        {
            title: translate(Content.map7.exports.remarks.title),
            id: "remarks",
            type: "text-area",
            placeholder: translate(Content.map7.exports.remarks.placeholder),
            error: isRemarksTooLong ? translate(Content.map7.exports.remarks.error) : "",
        },
    ];

    const getDialogDimensions: (heightOrWidth: "height" | "width") => string = (heightOrWidth) => {
        // mobile
        if (screenWidth < screenSizeBreakpoints.medium) {
            return heightOrWidth === "width" ? "85vw" : "85vh";
        }
        // tablet - large screen
        if (screenWidth < screenSizeBreakpoints.extraExtraLarge) {
            return heightOrWidth === "width" ? "70vw" : "80vh";
        }
        // 4k monitors etc
        return heightOrWidth === "width" ? "65vw" : "80vh";
    };

    const revertScaleToCustom = () => {
        setSelectedFormOption((state) => ({ ...state, scale: "Custom" }));
    };

    return (
        <StyledCustomDialog $screenWidth={screenWidth} $minWidth={getDialogDimensions("width")} $staticHeight={getDialogDimensions("height")} open={isOpen} onClose={onClose}>
            <DialogTitle>{map?.name}</DialogTitle>
            <Divider />
            <DialogContent>
                <ExportBody>
                    <ExportMapForm handleChange={handleChange} selectedFormOption={selectedFormOption} formOptions={formOptions} />
                    <MapPreview
                        initialState={initialState}
                        layersWithTemporaryVisibilityValue={layersWithTemporaryVisibilityValue}
                        mapApiFunctions={mapApiFunctions}
                        signatures={signatures}
                        ref={mapPreviewRef}
                        getMapStartingPosition={getMapStartingPosition}
                        exportFormValues={selectedFormOption}
                        map={map}
                        projectLogo={selectedFormOption.customLogo === null ? projectLogo : selectedFormOption.customLogo}
                        projectName={projectName}
                        userFirstName={userFirstName}
                        userLastName={userLastName}
                        selectedSitemapVersions={selectedSitemapVersions}
                        zoomChanged={revertScaleToCustom}
                        pitchChanged={revertScaleToCustom}
                        userId={userId}
                    />
                </ExportBody>
            </DialogContent>
            <Divider />
            <DialogActions>
                <FormButtonsComponent
                    submitButtonText={translate(Content.common.buttons.confirm)}
                    handleSubmit={handleConfirm}
                    disableSubmit={() => isRemarksTooLong}
                    isSubmitting={isSendingExportRequest}
                    secondaryButtons={[{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: onClose }]}
                />
            </DialogActions>
        </StyledCustomDialog>
    );
};

const ExportBody = styled.div`
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-columns: auto;
    overflow-y: hidden;
    ${media.medium} {
        grid-template-columns: 1fr 3fr;
        gap: 10px;
    }
`;
